import useTheme from "../../../../common/hooks/useTheme";
import PlatformTable from "./PlatformTable";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import Masonry from "react-masonry-css";
import {
  trainingBreakdownType,
  workoutItemType,
} from "../../../../providers/dataPlatformReportTypes";
import { Theme } from "../../../../providers/ThemeProvider";
import { getBiomarkerCategoryColor } from "../../store/helper";
import React from "react";

type InputArray = string[][];

interface OutputObject {
  [key: string]: string;
}

const WorkoutPlanTab = () => {
  const theme = useTheme();
  const reportData = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.programming.workoutPlan
  );

  if (
    reportData.workout.length === 0 ||
    reportData.trainingBreakdown.length === 0
  ) {
    return <div style={styles.container}>No workout data available.</div>;
  }

  const trainingBreakdownChartData = {
    title: reportData.trainingBreakdownLabel,
    pieData: reportData.trainingBreakdown.map((el: trainingBreakdownType) => {
      return {
        categoryType: el.index,
        unit: "kcal",
        value: el.kcal,
        label: el.name,
      };
    }),
  };

  const convertArrayToObjects = (data: InputArray): OutputObject[] => {
    return data.map((row: string[]) =>
      row.reduce((acc: OutputObject, cell: string, index: number) => {
        acc[`c${index + 1}`] = cell;
        return acc;
      }, {} as OutputObject)
    );
  };

  const WorkoutSection = ({
    title,
    workouts,
  }: {
    title: string;
    workouts: workoutItemType[];
  }) => {
    return (
      <div>
        <div style={styles.title(theme)}>{title}</div>
        <Masonry
          breakpointCols={{ default: 2 }}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {workouts.map((workout, index) => (
            <PlatformTable
              key={index}
              data={convertArrayToObjects(workout.exercises)}
              details={workout.detail}
              type={workout.type}
            />
          ))}
        </Masonry>
      </div>
    );
  };

  const getNormalizedDataForPieChart = (data: any) => {
    const percentage1 = Math.round(
      (data[0].value / (data[0].value + data[1].value + data[2].value)) * 100
    );
    const percentage3 = Math.round(
      (data[2].value / (data[0].value + data[1].value + data[2].value)) * 100
    );

    return data.map((el: any) => {
      if (el.categoryType === 2) {
        return {
          label: el.label,
          value: el.value,
          categoryType: el.categoryType,
          percentage: 100 - percentage1 - percentage3,
        };
      } else {
        return {
          label: el.label,
          value: el.value,
          categoryType: el.categoryType,
          percentage: el.categoryType === 1 ? percentage1 : percentage3,
        };
      }
    });
  };

  const workoutData = getNormalizedDataForPieChart(
    trainingBreakdownChartData.pieData
  );

  const totalWorkoutCalories = workoutData.reduce(
    (sum: number, el: any) => sum + el.value,
    0
  );

  const workoutsByType = reportData.workout.reduce((acc, workout) => {
    if (!acc[workout.type]) acc[workout.type] = [];
    acc[workout.type].push(workout);
    return acc;
  }, {} as Record<number, workoutItemType[]>);

  const cardioWorkout = workoutsByType[3] || [];
  const intervalWorkout = workoutsByType[1] || [];
  const resistanceWorkout = workoutsByType[2] || [];

  return (
    <div style={styles.container}>
      <div
        style={{
          color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          fontFamily: "var(--font-Medium)",
          fontSize: "20px",
          lineHeight: "28px",
        }}
      >
        Workout Plan
      </div>
      <div>
        <div style={styles.title(theme)}>Training Breakdown</div>
        <div style={styles.barContainer(theme)}>
          <div style={styles.workoutTitle(theme)}>
            Weekly kcal:{" "}
            <span style={styles.value(theme)}>{totalWorkoutCalories}</span>{" "}
            <span style={styles.unit(theme)}>kcal</span>
          </div>
          <div style={styles.bar(theme)}>
            <div
              style={styles.barInner(
                theme.colorTokens.W[50],
                `${
                  workoutData.find((el: any) => el.categoryType === 1)
                    .percentage +
                  workoutData.find((el: any) => el.categoryType === 2)
                    .percentage
                }%`
              )}
            />
            <div
              style={styles.barInner(
                theme.colorTokens.D[50],
                `${
                  workoutData.find((el: any) => el.categoryType === 1)
                    .percentage
                }%`
              )}
            />
          </div>
          <div style={styles.legendContainer}>
            {workoutData.map((el: any, index: number) => (
              <div key={index} style={styles.legendItem}>
                <div
                  style={styles.dot(
                    getBiomarkerCategoryColor(
                      el.categoryType,
                      theme,
                      "trainingBreakdown"
                    ) ?? ""
                  )}
                />
                <div style={styles.unit(theme)}>{el.label}</div>
                <div style={styles.unit(theme)}>
                  <span style={styles.value(theme)}>{el.percentage}%</span>
                  {" - "}
                  <span style={styles.value(theme)}>{el.value}</span>{" "}
                  <span>kcal</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        {cardioWorkout.length > 0 && (
          <WorkoutSection
            title={cardioWorkout[0].title}
            workouts={cardioWorkout}
          />
        )}

        {intervalWorkout.length > 0 && (
          <WorkoutSection
            title={intervalWorkout[0].title}
            workouts={intervalWorkout}
          />
        )}
        {resistanceWorkout.length > 0 && (
          <WorkoutSection
            title={resistanceWorkout[0].title}
            workouts={resistanceWorkout}
          />
        )}
      </div>
      {/* The Training Program (zones) table moved to the Health overview's
          Energy & Training Zones system (SD-6793). */}
    </div>
  );
};

export default WorkoutPlanTab;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  },
  title: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    marginBottom: "16px",
  }),
  barContainer: (theme: Theme) => ({
    padding: "8px 12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  }),
  bar: (theme: Theme) => ({
    width: "100%",
    height: "16px",
    borderRadius: "38px",
    backgroundColor: theme.colorTokens.P[60],
    position: "relative" as "relative",
    overflow: "hidden",
  }),
  workoutTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
  }),
  value: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
  }),
  unit: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
  }),
  barInner: (color: string, width: string) => ({
    height: "100%",
    borderRadius: "38px",
    position: "absolute" as "absolute",
    width: width,
    backgroundColor: color,
  }),
  legendContainer: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
  },
  dot: (color: string) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: color,
  }),
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
};
